// src/pages/LandingPage/Plataforma/Correspondencia/SeguimientoPublicoPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
    Package, Shield, QrCode, Copy, Check, 
    Printer, Clock, CheckCircle2, AlertCircle, ExternalLink,
    Camera, Eye, X, ChevronRight, UserCheck
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { fetchSeguimientoPublico, Correspondencia } from '../../../../services/correspondencia';
import { useTheme } from '../../../../context/ThemeContext';


const SeguimientoPublicoPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const { isDark } = useTheme();
    const [correspondencia, setCorrespondencia] = useState<Correspondencia | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [copiadoPin, setCopiadoPin] = useState(false);
    const [copiadoCodigo, setCopiadoCodigo] = useState(false);
    const [fotoModal, setFotoModal] = useState<string | null>(null);

    useEffect(() => {
        if (!token) return;
        const loadTracking = async () => {
            setLoading(true);
            setError(null);
            try {
                // Se envía el parámetro limpio
                const data = await fetchSeguimientoPublico(token);
                setCorrespondencia(data);
            } catch (err: any) {
                console.error('Error cargando seguimiento:', err);
                setError('No encontramos ninguna correspondencia o encomienda con el código o enlace proporcionado.');
            } finally {
                setLoading(false);
            }
        };
        loadTracking();
    }, [token]);

    const copiarTexto = (texto: string, tipo: 'pin' | 'codigo') => {
        navigator.clipboard.writeText(texto);
        if (tipo === 'pin') {
            setCopiadoPin(true);
            setTimeout(() => setCopiadoPin(false), 2000);
        } else {
            setCopiadoCodigo(true);
            setTimeout(() => setCopiadoCodigo(false), 2000);
        }
    };

    const formatDate = (dateString: string | null): string => {
        if (!dateString) return 'Pendiente';
        const d = new Date(dateString);
        return d.toLocaleDateString('es-BO', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getEstadoInfo = (estado: string) => {
        switch (estado) {
            case 'recibido':
                return {
                    label: 'En Recepción Comarca',
                    badgeColor: isDark ? 'bg-purple-950/60 text-purple-300 border-purple-800/60' : 'bg-purple-100 text-purple-800 border-purple-200',
                    desc: 'El paquete se encuentra en recepción bajo custodia, listo para ser procesado.'
                };
            case 'notificado':
                return {
                    label: 'Listo para Retiro',
                    badgeColor: isDark ? 'bg-amber-950/60 text-amber-300 border-amber-800/60' : 'bg-amber-100 text-amber-800 border-amber-200',
                    desc: 'El paquete está clasificado y listo para ser retirado en recepción de Comarca.'
                };
            case 'entregado':
                return {
                    label: 'Entregado con Éxito',
                    badgeColor: isDark ? 'bg-emerald-950/60 text-emerald-300 border-emerald-800/60' : 'bg-emerald-100 text-emerald-800 border-emerald-200',
                    desc: 'El paquete ya fue retirado por el destinatario o una persona autorizada.'
                };
            case 'cancelado':
                return {
                    label: 'Cancelado',
                    badgeColor: isDark ? 'bg-rose-950/60 text-rose-300 border-rose-800/60' : 'bg-rose-100 text-rose-800 border-rose-200',
                    desc: 'Esta encomienda fue cancelada o devuelta.'
                };
            default:
                return {
                    label: estado,
                    badgeColor: isDark ? 'bg-gray-800 text-gray-300 border-gray-700' : 'bg-gray-100 text-gray-800 border-gray-200',
                    desc: ''
                };
        }
    };

    return (
        <div 
            className="min-h-screen py-8 px-4 font-['DM_Sans',sans-serif] transition-colors"
            style={{
                background: isDark ? '#0d0519' : 'linear-gradient(135deg, #f8fafc, #f1f5f9, #faf5ff)'
            }}
        >
            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    #print-section, #print-section * { visibility: visible; }
                    #print-section { position: absolute; left: 0; top: 0; width: 100%; }
                }
            `}</style>

            <div className="max-w-xl mx-auto space-y-6">
                {/* Header Marca */}
                <div className="text-center space-y-1">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-tr from-[#61257B] to-[#C1348B] text-white shadow-md shadow-purple-900/30 mb-2">
                        <Package className="w-6 h-6" />
                    </div>
                    <h1 className="text-xl font-black tracking-tight" style={{ color: isDark ? '#ffffff' : '#111827' }}>Comarca Coworking</h1>
                    <p className="text-xs font-medium" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>Servicio Oficial de Recepción y Encomiendas</p>
                </div>

                {loading ? (
                    <div 
                        className="rounded-3xl p-10 text-center shadow-xl border space-y-4 animate-pulse"
                        style={{
                            background: isDark ? 'rgba(26, 11, 46, 0.85)' : '#ffffff',
                            borderColor: isDark ? 'rgba(193, 52, 139, 0.25)' : '#f3f4f6'
                        }}
                    >
                        <div className="w-12 h-12 border-4 border-[#61257B] border-t-transparent rounded-full animate-spin mx-auto" />
                        <p className="text-sm font-semibold" style={{ color: isDark ? '#cbd5e1' : '#4b5563' }}>Consultando estado de la encomienda...</p>
                    </div>
                ) : error || !correspondencia ? (
                    <div 
                        className="rounded-3xl p-8 text-center shadow-xl border space-y-4"
                        style={{
                            background: isDark ? 'rgba(26, 11, 46, 0.85)' : '#ffffff',
                            borderColor: isDark ? 'rgba(193, 52, 139, 0.25)' : '#f3f4f6'
                        }}
                    >
                        <div className="w-14 h-14 bg-red-100 dark:bg-red-950/50 text-red-600 rounded-full flex items-center justify-center mx-auto">
                            <AlertCircle className="w-8 h-8" />
                        </div>
                        <h3 className="text-lg font-bold" style={{ color: isDark ? '#ffffff' : '#111827' }}>Correspondencia No Encontrada</h3>
                        <p className="text-xs leading-relaxed" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>{error}</p>
                        <p className="text-[11px]" style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>Verifica que el enlace o código sea correcto.</p>
                    </div>
                ) : (
                    <div className="space-y-5" id="print-section">
                        {/* Comprobante Principal */}
                        <div 
                            className="rounded-3xl shadow-xl border overflow-hidden"
                            style={{
                                background: isDark ? 'rgba(26, 11, 46, 0.85)' : '#ffffff',
                                borderColor: isDark ? 'rgba(193, 52, 139, 0.25)' : '#f3f4f6'
                            }}
                        >
                            {/* Cabecera Comprobante */}
                            <div className="bg-gradient-to-r from-[#61257B] to-[#C1348B] p-5 text-white text-center relative">
                                <span className="text-[11px] font-bold uppercase tracking-widest bg-white/20 px-3 py-1 rounded-full inline-block mb-1">
                                    Seguimiento de Encomienda
                                </span>
                                <h2 className="text-2xl font-black font-mono mt-1">
                                    {correspondencia.codigo || `#${correspondencia.id}`}
                                </h2>
                                <p className="text-xs text-white/80 mt-1">
                                    Para retirar por: <span className="font-bold text-white">{correspondencia.destinatario_nombre || 'Cliente'}</span>
                                </p>
                            </div>

                            <div className="p-6 space-y-6">
                                {/* Estado actual */}
                                <div className="text-center space-y-1 pb-2 border-b" style={{ borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6' }}>
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getEstadoInfo(correspondencia.estado).badgeColor}`}>
                                        {getEstadoInfo(correspondencia.estado).label}
                                    </span>
                                    <p className="text-xs" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>
                                        {getEstadoInfo(correspondencia.estado).desc}
                                    </p>
                                </div>

                                {/* OPCIONES DE RETIRO (Solo si no está entregado) */}
                                {correspondencia.estado !== 'entregado' && (
                                    <div className="space-y-4">
                                        {/* Opción 1: Código QR */}
                                        <div 
                                            className="p-5 rounded-2xl border text-center space-y-3"
                                            style={{
                                                background: isDark ? 'rgba(15, 7, 28, 0.7)' : '#f9fafb',
                                                borderColor: isDark ? 'rgba(193, 52, 139, 0.25)' : '#e5e7eb'
                                            }}
                                        >
                                            <div className="flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider" style={{ color: isDark ? '#ffffff' : '#374151' }}>
                                                <QrCode className="w-4 h-4 text-[#C1348B]" />
                                                1. Muestra este QR al recepcionista
                                            </div>
                                            <div className="bg-white p-4 rounded-2xl shadow-sm inline-block border border-gray-200">
                                                <QRCodeSVG
                                                    value={correspondencia.token_verificacion || correspondencia.codigo || String(correspondencia.id)}
                                                    size={180}
                                                    level="H"
                                                    includeMargin={true}
                                                />
                                            </div>
                                            <p className="text-[11px]" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>
                                                El recepcionista lo escaneará en recepción para entregarte el paquete al instante.
                                            </p>
                                        </div>

                                        {/* Opción 2: PIN Secreto de Retiro */}
                                        {correspondencia.pin_seguridad && (
                                            <div 
                                                className="p-4 rounded-2xl border text-center space-y-2"
                                                style={{
                                                    background: isDark ? 'linear-gradient(135deg, rgba(97,37,123,0.2), rgba(193,52,139,0.15))' : 'linear-gradient(135deg, rgba(97,37,123,0.06), rgba(193,52,139,0.06))',
                                                    borderColor: isDark ? 'rgba(193, 52, 139, 0.35)' : 'rgba(97, 37, 123, 0.2)'
                                                }}
                                            >
                                                <span className="text-[11px] font-bold uppercase tracking-wider flex items-center justify-center gap-1" style={{ color: isDark ? '#ffd97c' : '#61257B' }}>
                                                    <Shield className="w-3.5 h-3.5 text-[#C1348B]" />
                                                    2. O dicta tu PIN Secreto de Retiro
                                                </span>
                                                <div className="flex items-center justify-center gap-2">
                                                    <span 
                                                        className="text-3xl font-mono font-black tracking-widest px-4 py-1.5 rounded-xl border shadow-inner"
                                                        style={{
                                                            background: isDark ? 'rgba(15, 7, 28, 0.9)' : '#ffffff',
                                                            color: isDark ? '#ffd97c' : '#61257B',
                                                            borderColor: isDark ? 'rgba(193, 52, 139, 0.35)' : 'rgba(97, 37, 123, 0.25)'
                                                        }}
                                                    >
                                                        {correspondencia.pin_seguridad}
                                                    </span>
                                                    <button
                                                        onClick={() => copiarTexto(correspondencia.pin_seguridad || '', 'pin')}
                                                        className="p-2.5 rounded-xl border shadow-sm transition-colors"
                                                        style={{
                                                            background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#ffffff',
                                                            color: isDark ? '#ffffff' : '#61257B',
                                                            borderColor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(97, 37, 123, 0.25)'
                                                        }}
                                                        title="Copiar PIN"
                                                    >
                                                        {copiadoPin ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* === PROCESO VISUAL DE ENVÍO Y EVIDENCIAS === */}
                                <div 
                                    className="p-5 border rounded-2xl space-y-5"
                                    style={{
                                        background: isDark ? 'rgba(15, 7, 28, 0.5)' : '#f8fafc',
                                        borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0'
                                    }}
                                >
                                    <div 
                                        className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider border-b pb-2"
                                        style={{ borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0', color: isDark ? '#ffffff' : '#1f2937' }}
                                    >
                                        <Clock className="w-4 h-4 text-[#C1348B]" />
                                        Proceso de Envío y Evidencias
                                    </div>

                                    {/* Timeline */}
                                    <div className="relative pl-6 space-y-6 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-gray-200 dark:before:bg-gray-800">
                                        {/* Paso 1: Registrado */}
                                        <div className="relative">
                                            <div className="absolute -left-6 top-0 w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px] font-bold shadow">
                                                ✓
                                            </div>
                                            <div className="text-xs">
                                                <span className="font-bold block text-xs" style={{ color: isDark ? '#ffffff' : '#111827' }}>1. Encomienda Registrada</span>
                                                <p className="mt-0.5" style={{ color: isDark ? '#cbd5e1' : '#4b5563' }}>
                                                    Dejada por: <strong style={{ color: isDark ? '#ffffff' : '#1f2937' }}>{correspondencia.remitente_nombre} {correspondencia.remitente_apellido || ''}</strong>
                                                </p>
                                                {correspondencia.descripcion && (
                                                    <p className="text-[11px] mt-0.5" style={{ color: isDark ? '#94a3b8' : '#6b7280' }}>
                                                        Contenido: {correspondencia.descripcion}
                                                    </p>
                                                )}
                                                <span className="text-[10px] block mt-0.5" style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>
                                                    Fecha: {formatDate(correspondencia.fecha_creacion || correspondencia.fecha_recepcion)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Paso 2: Recibido en Recepción Comarca */}
                                        <div className="relative">
                                            <div className={`absolute -left-6 top-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shadow ${
                                                correspondencia.estado !== 'cancelado' ? 'bg-[#61257B] text-white' : 'bg-gray-300 dark:bg-gray-700 text-gray-500'
                                            }`}>
                                                {correspondencia.estado === 'entregado' ? '✓' : '2'}
                                            </div>
                                            <div className="text-xs space-y-2">
                                                <div>
                                                    <span className="font-bold block" style={{ color: isDark ? '#ffffff' : '#111827' }}>2. En Custodia en Recepción Comarca</span>
                                                    <p className="mt-0.5" style={{ color: isDark ? '#cbd5e1' : '#4b5563' }}>
                                                        Guardado bajo custodia segura en la sede Comarca Coworking.
                                                    </p>
                                                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]" style={{ color: isDark ? '#cbd5e1' : '#4b5563' }}>
                                                        <span className="flex items-center gap-1 font-medium">
                                                            <UserCheck className="w-3.5 h-3.5 text-[#C1348B]" />
                                                            Recepcionado por: <strong style={{ color: isDark ? '#ffffff' : '#1f2937' }}>{correspondencia.usuario_recibe_nombre || 'Recepción Comarca Coworking'}</strong>
                                                        </span>
                                                        <span className="text-gray-400">•</span>
                                                        <span style={{ color: isDark ? '#94a3b8' : '#6b7280' }}>
                                                            Fecha Recepción: {formatDate(correspondencia.fecha_recepcion)}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Foto de Evidencia de Recepción */}
                                                {correspondencia.imagen ? (
                                                    <div 
                                                        className="p-2.5 rounded-xl space-y-1.5 border"
                                                        style={{
                                                            background: isDark ? 'rgba(26, 11, 46, 0.7)' : '#ffffff',
                                                            borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e5e7eb'
                                                        }}
                                                    >
                                                        <span className="text-[10px] font-bold flex items-center gap-1 uppercase tracking-wider" style={{ color: isDark ? '#ffd97c' : '#374151' }}>
                                                            <Camera className="w-3.5 h-3.5 text-[#C1348B]" />
                                                            Foto de Evidencia en Recepción:
                                                        </span>
                                                        <div 
                                                            className="relative h-44 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 cursor-pointer group border border-gray-200 dark:border-gray-700"
                                                            onClick={() => setFotoModal(correspondencia.imagen || null)}
                                                        >
                                                            <img 
                                                                src={correspondencia.imagen} 
                                                                alt="Evidencia Recepción" 
                                                                className="w-full h-full object-contain group-hover:scale-105 transition-transform" 
                                                            />
                                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-xs font-semibold gap-1 transition-opacity">
                                                                <Eye className="w-4 h-4" />
                                                                Click para Ampliar Foto
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="text-[11px] italic block" style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>
                                                        (Evidencia fotográfica en espera de recepción física)
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Paso 3: Entrega al Destinatario */}
                                        <div className="relative">
                                            <div className={`absolute -left-6 top-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shadow ${
                                                correspondencia.estado === 'entregado' 
                                                    ? 'bg-emerald-500 text-white' 
                                                    : 'bg-gray-200 dark:bg-gray-800 text-gray-500'
                                            }`}>
                                                {correspondencia.estado === 'entregado' ? '✓' : '3'}
                                            </div>
                                            <div className="text-xs space-y-2">
                                                <div>
                                                    <span className="font-bold block" style={{ color: isDark ? '#ffffff' : '#111827' }}>3. Entrega al Destinatario</span>
                                                    {correspondencia.estado === 'entregado' ? (
                                                        <div className="mt-0.5 space-y-1">
                                                            <p className="text-emerald-500 font-bold flex items-center gap-1">
                                                                <CheckCircle2 className="w-3.5 h-3.5" />
                                                                Entregado a: {correspondencia.receptor_nombre} {correspondencia.receptor_apellido || ''}
                                                            </p>
                                                            {correspondencia.usuario_entrega_nombre && (
                                                                <p className="text-[11px] flex items-center gap-1" style={{ color: isDark ? '#cbd5e1' : '#4b5563' }}>
                                                                    <UserCheck className="w-3.5 h-3.5 text-emerald-500" />
                                                                    Entregado por personal: <strong style={{ color: isDark ? '#ffffff' : '#1f2937' }}>{correspondencia.usuario_entrega_nombre}</strong>
                                                                </p>
                                                            )}
                                                            {correspondencia.receptor_ci_nit && (
                                                                <span className="text-[11px] block" style={{ color: isDark ? '#cbd5e1' : '#6b7280' }}>
                                                                    CI Receptor: {correspondencia.receptor_ci_nit}
                                                                </span>
                                                            )}
                                                            <span className="text-[10px] block" style={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>
                                                                Fecha de Entrega: {formatDate(correspondencia.fecha_entrega)}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <p className="text-amber-500 font-medium mt-0.5">
                                                            Pendiente de retiro con PIN en recepción. Destinatario: <strong>{correspondencia.destinatario_nombre || 'Cliente'}</strong>
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Foto de Evidencia de Entrega */}
                                                {correspondencia.estado === 'entregado' && correspondencia.imagen_entrega && (
                                                    <div className={`p-2.5 rounded-xl space-y-1.5 border ${isDark ? 'bg-emerald-950/40 border-emerald-800/50' : 'bg-emerald-50/70 border-emerald-200'}`}>
                                                        <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-300 flex items-center gap-1 uppercase tracking-wider">
                                                            <Camera className="w-3.5 h-3.5 text-emerald-500" />
                                                            Foto de Respaldo de Entrega:
                                                        </span>
                                                        <div 
                                                            className="relative h-44 rounded-lg overflow-hidden bg-white dark:bg-gray-800 cursor-pointer group border border-emerald-200 dark:border-emerald-800"
                                                            onClick={() => setFotoModal(correspondencia.imagen_entrega || null)}
                                                        >
                                                            <img 
                                                                src={correspondencia.imagen_entrega} 
                                                                alt="Evidencia Entrega" 
                                                                className="w-full h-full object-contain group-hover:scale-105 transition-transform" 
                                                            />
                                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-xs font-semibold gap-1 transition-opacity">
                                                                <Eye className="w-4 h-4" />
                                                                Click para Ampliar Foto
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Botones Compartir WhatsApp / Imprimir */}
                                <div className="space-y-2 pt-2">
                                    <a
                                        href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                                            `📦 Seguimiento de encomienda en Comarca Coworking:\nCódigo: ${correspondencia.codigo || `#${correspondencia.id}`}\nEstado: ${correspondencia.estado.toUpperCase()}\nVer detalles y PIN: ${window.location.href}`
                                        )}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full py-3 px-4 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold text-xs rounded-xl flex items-center justify-center gap-2 shadow-sm transition-all"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                        Compartir Seguimiento por WhatsApp
                                    </a>

                                    <button
                                        onClick={() => window.print()}
                                        className="w-full py-2.5 px-4 font-semibold text-xs rounded-xl flex items-center justify-center gap-2 transition-colors"
                                        style={{
                                            background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6',
                                            color: isDark ? '#ffffff' : '#374151',
                                            border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : 'none'
                                        }}
                                    >
                                        <Printer className="w-4 h-4" />
                                        Imprimir / Guardar Comprobante PDF
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal para ver foto ampliada */}
            {fotoModal && (
                <div 
                    className="fixed inset-0 bg-black/80 z-[999] flex items-center justify-center p-4 backdrop-blur-sm"
                    onClick={() => setFotoModal(null)}
                >
                    <div 
                        className="relative max-w-2xl w-full rounded-2xl overflow-hidden shadow-2xl p-4 space-y-3"
                        style={{
                            background: isDark ? '#150a26' : '#ffffff',
                            border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between border-b pb-2" style={{ borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6' }}>
                            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: isDark ? '#ffffff' : '#1f2937' }}>
                                Fotografía de Evidencia
                            </span>
                            <button 
                                onClick={() => setFotoModal(null)} 
                                className="p-1 rounded-lg transition-colors"
                                style={{
                                    background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6',
                                    color: isDark ? '#cbd5e1' : '#6b7280'
                                }}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="max-h-[70vh] flex items-center justify-center rounded-xl overflow-hidden" style={{ background: isDark ? 'rgba(0,0,0,0.3)' : '#f9fafb' }}>
                            <img src={fotoModal} alt="Evidencia" className="max-h-[68vh] max-w-full object-contain" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SeguimientoPublicoPage;
